import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { brands, images } from '../mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Building2, Users, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(2, 31, 63, 0.85), rgba(10, 61, 98, 0.90)), url(${images.heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-[#021F3F]/20" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-tight">
              IndustryTOP
              <span className="block text-[#D4AF37] mt-2">Consulting LLC-FZ</span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-gray-200 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed px-4">
              {t.home.slogan}
            </p>
            <p className="text-sm sm:text-lg text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-4">
              {t.home.description}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#E6C76D] text-[#021F3F] font-semibold px-8 py-6 text-lg rounded-lg transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
              >
                {t.home.ctaButton}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#D4AF37] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Extended Description Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            {t.home.descriptionExtended}
          </p>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-4">
              {t.home.advantagesTitle}
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.home.advantages.map((advantage, index) => (
              <Card key={index} className="p-8 border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E6C76D] flex items-center justify-center mb-6 mx-auto">
                  <span className="text-[#021F3F] text-2xl font-bold">{index + 1}</span>
                </div>
                <h3 className="text-2xl font-bold text-[#021F3F] mb-4 text-center">{advantage.title}</h3>
                <p className="text-gray-600 leading-relaxed text-center">{advantage.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Brands Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-4">
              {t.home.brandsTitle}
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t.home.brandsSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {brands.map((brand) => (
              <Card
                key={brand.id}
                className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                {/* Brand Logo Card */}
                <div className="relative h-64 overflow-hidden bg-white flex items-center justify-center p-8">
                  {/* Logo Container */}
                  <div className="w-40 h-40 flex items-center justify-center transform group-hover:scale-105 transition-transform duration-500">
                    {/* Logo Image - scaled to 95% */}
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-w-full max-h-full object-contain"
                      style={{ transform: 'scale(0.95)' }}
                    />
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {brand.description[language]}
                  </p>
                  <a href={brand.link} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      className="w-full border-[#0A3D62] text-[#0A3D62] hover:bg-[#0A3D62] hover:text-white transition-all duration-300"
                    >
                      {t.brands.viewProject}
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </a>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Dubai Section */}
      <section className="py-20 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-6">
                {t.home.whyDubaiTitle}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                {t.home.whyDubaiText}
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                {t.home.whyDubaiExtended}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: Building2, text: language === 'ro' ? 'Hub Global' : 'Global Hub' },
                  { icon: Users, text: language === 'ro' ? 'Rețea Internațională' : 'International Network' },
                  { icon: Lightbulb, text: language === 'ro' ? 'Inovație' : 'Innovation' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-4 rounded-lg bg-[#F8F8F8] hover:bg-[#0A3D62]/5 transition-colors duration-300"
                    >
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E6C76D] flex items-center justify-center flex-shrink-0">
                        <Icon className="text-[#021F3F]" size={24} />
                      </div>
                      <span className="font-semibold text-[#021F3F]">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img
                  src="https://images.pexels.com/photos/618079/pexels-photo-618079.jpeg"
                  alt="Dubai"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-[#D4AF37]/20 rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-48 h-48 bg-[#0A3D62]/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#021F3F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0A3D62] rounded-full filter blur-3xl" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.home.ctaTitle}
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            {t.home.ctaText}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#D4AF37] hover:bg-[#E6C76D] text-[#021F3F] font-semibold px-8 py-6 text-lg rounded-lg transform hover:scale-105 transition-all duration-300 shadow-xl"
            >
              {t.home.ctaButton}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;